import {
  DashboardAlphaReportStatus,
  DashboardAlphaReportStatusEnum,
} from '@ulysses-inc/harami_api_client'
import { addDays, format, getDay, parseISO } from 'date-fns'
import { demoReportUuidPrefix } from 'src/features/monitoringDashboardAlpha/demo/demoReportLink'
import {
  DemoSchedule,
  DemoTier,
  demoProfile,
} from 'src/features/monitoringDashboardAlpha/demo/profile'
import {
  hashString,
  seededRandom,
} from 'src/features/monitoringDashboardAlpha/demo/random'

/** 今日の「期間中」の行のうち、一時保存まで進めている行の割合（earlyImplemented に足して判定する） */
const recordingRateDuringPeriod = 0.15

/** スケジュールに曜日の指定がないときに実施する曜日（月〜土。日曜は休み） */
const defaultWeekdays = [1, 2, 3, 4, 5, 6]

/**
 * 現場 × スケジュール × 日付の 1 行。
 * API の DashboardAlphaReportStatus に、現場グループでの絞り込み用の placeGroupUuid を足したもの。
 */
export type DemoReportStatusRow = DashboardAlphaReportStatus & {
  /** 所属する現場グループ。API の行には含まれない内部用の値 */
  placeGroupUuid: string | null
}

type Params = {
  /** 対象期間の開始日（YYYY-MM-DD。この日を含む） */
  startDate: string
  /** 対象期間の終了日（YYYY-MM-DD。この日を含む） */
  endDate: string
  /** 現在時刻。今日の行が「期間中」か「締切後」かの判定にだけ使う */
  now: Date
  /** 実在するレポートの UUID。空ならデモ用の UUID を付ける */
  showcaseReportUuids?: readonly string[]
}

const reportExistsStatuses: readonly DashboardAlphaReportStatusEnum[] = [
  DashboardAlphaReportStatusEnum.Implemented,
  DashboardAlphaReportStatusEnum.ImplementedAfterDeadline,
  DashboardAlphaReportStatusEnum.Recording,
  DashboardAlphaReportStatusEnum.RecordingOverdue,
]

/**
 * 1 行分のステータスを決める。
 * harami_api_server の newDashboardAlphaDailyStatus と同じ区分（開始前・期間中・締切後）で、
 * 実際のレポートの代わりに、現場 × 日付の中での分位 quantile と現場の傾向 tier で結果を決める。
 *
 * 行ごとに独立の乱数で決めず分位にしているのは、1 日あたりの各ステータスの件数が
 * 「割合 × 件数」から 1 件未満しかずれないようにして、どの期間を見ても現場の傾向が狙いどおりに出るようにするため。
 * 期間中は分位の上側、締切後は下側から見るので、期間中に実施済みの行は締切後も実施済みのままになり、
 * 今日の行が時間経過で矛盾しない。
 */
const decideStatus = ({
  date,
  today,
  nowHour,
  schedule,
  tier,
  quantile,
}: {
  date: string
  today: string
  /** 現在時刻（時。分は小数で含める） */
  nowHour: number
  schedule: DemoSchedule
  tier: DemoTier
  /** その現場・その日に出るスケジュールの中での位置（0 以上 1 未満） */
  quantile: number
}): DashboardAlphaReportStatusEnum => {
  // 日付は YYYY-MM-DD なので、文字列比較で日付の前後が分かる
  if (date > today) return DashboardAlphaReportStatusEnum.NotStarted

  if (date === today) {
    if (nowHour < schedule.startHour) {
      return DashboardAlphaReportStatusEnum.NotStarted
    }
    if (nowHour < schedule.deadlineHour) {
      if (quantile >= 1 - tier.earlyImplemented) {
        return DashboardAlphaReportStatusEnum.Implemented
      }
      if (quantile >= 1 - tier.earlyImplemented - recordingRateDuringPeriod) {
        return DashboardAlphaReportStatusEnum.Recording
      }
      return DashboardAlphaReportStatusEnum.DuringPeriod
    }
  }

  // 過去の日、または今日で締切を過ぎた行
  const notImplementedYetUpTo = tier.notImplementedYet
  const recordingOverdueUpTo = notImplementedYetUpTo + tier.recordingOverdue
  const implementedAfterDeadlineUpTo =
    recordingOverdueUpTo + tier.implementedAfterDeadline
  if (quantile < notImplementedYetUpTo) {
    return DashboardAlphaReportStatusEnum.NotImplementedYet
  }
  if (quantile < recordingOverdueUpTo) {
    return DashboardAlphaReportStatusEnum.RecordingOverdue
  }
  if (quantile < implementedAfterDeadlineUpTo) {
    return DashboardAlphaReportStatusEnum.ImplementedAfterDeadline
  }
  return DashboardAlphaReportStatusEnum.Implemented
}

/** レポートが存在する行に付ける UUID。実在レポートの指定があればその中から、無ければデモ用のダミーを返す */
const buildReportUuid = (
  seed: string,
  showcaseReportUuids: readonly string[],
): string => {
  const hash = hashString(`report|${seed}`)
  const showcaseReportUuid =
    showcaseReportUuids.length > 0
      ? showcaseReportUuids[hash % showcaseReportUuids.length]
      : undefined
  if (showcaseReportUuid) return showcaseReportUuid
  return `${demoReportUuidPrefix}${hash.toString(16).padStart(8, '0')}`
}

/** startDate〜endDate の各日（ローカル日付）を YYYY-MM-DD と曜日で列挙する */
const listDays = (startDate: string, endDate: string) => {
  const days: { date: string; weekday: number }[] = []
  const last = parseISO(endDate)
  for (let day = parseISO(startDate); day <= last; day = addDays(day, 1)) {
    days.push({ date: format(day, 'yyyy-MM-dd'), weekday: getDay(day) })
  }
  return days
}

/**
 * 期間内の全現場について、現場 × スケジュール × 日付の行を生成する。
 *
 * 結果は現場・スケジュール・日付だけから決まるため、同じ入力なら同じ結果になる
 * （now に依存するのは今日の行の「期間中」「締切後」の区分だけ）。
 * 絞り込み・並び・ページングは呼び出し側（demoDashboardAlphaApi）で行う。
 */
export const generateDemoReportStatuses = ({
  startDate,
  endDate,
  now,
  showcaseReportUuids = [],
}: Params): DemoReportStatusRow[] => {
  const today = format(now, 'yyyy-MM-dd')
  const nowHour = now.getHours() + now.getMinutes() / 60
  const days = listDays(startDate, endDate)

  const rows: DemoReportStatusRow[] = []
  for (const place of demoProfile.places) {
    const tier = demoProfile.tiers[place.tier]
    if (!tier) throw new Error(`Unknown demo tier: ${place.tier}`)
    const placeSchedules = demoProfile.schedules.filter(schedule =>
      schedule.kinds.includes(place.kind),
    )

    for (const { date, weekday } of days) {
      // 順位をつける母集団は、その日に出るスケジュールすべて。now や開始前かどうかに依存させず、
      // 時間が進んでも同じ行が同じ位置に居続けるようにする。
      // 並びは seededRandom（FNV-1a を混ぜ直した値）の昇順にする。FNV-1a の値のままだと日付が変わっても
      // 順位が固まり、同じスケジュールが毎日同じ結果になってしまう
      const ranked = placeSchedules
        .filter(schedule =>
          (schedule.weekdays ?? defaultWeekdays).includes(weekday),
        )
        .map(schedule => {
          const seed = `${place.uuid}|${schedule.uuid}|${date}`
          return { schedule, seed, order: seededRandom(seed) }
        })
        .sort((a, b) => a.order - b.order)
      // 日ごとの偏り。件数が端数になる日の切り上げ・切り捨てを日ごとに振り分け、平均が割合どおりになるようにする
      const bias = seededRandom(`${place.uuid}|${date}`)

      ranked.forEach(({ schedule, seed }, rank) => {
        const status = decideStatus({
          date,
          today,
          nowHour,
          schedule,
          tier,
          quantile: (rank + bias) / ranked.length,
        })
        rows.push({
          scheduleUUID: schedule.uuid,
          scheduleName: schedule.name,
          placeNodeUUID: place.uuid,
          placeName: place.name,
          placeGroupUuid: place.placeGroupUuid,
          date,
          status,
          ...(reportExistsStatuses.includes(status) && {
            reportUUID: buildReportUuid(seed, showcaseReportUuids),
          }),
        })
      })
    }
  }
  return rows
}
